import { FormSectionHandler } from '../entity.handler';
import { ThemeResponse, ThemeUpdateRequest, Dictionary } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { get, set } from 'lodash';
import { ComboField } from '../utils';
import {ThemeEngine } from 'app/shared/components/theme';
import { THEME_OFF_WHITE, THEME_OFF_BLACK } from 'app/shared/components/theme/theme-defaults';

interface EditorValues {
    [key: string]: any;
}

const fonts = ['Muli', 'Roboto', 'Open Sans', 'Source Sans Pro', 'Montserrat', 'Lato', 'Ubuntu', 'Arimo'];
const fontWeights = ['300', '400', '500', '600', '700'];

export abstract class ThemeSettingsBaseSectionHandler implements FormSectionHandler<ThemeResponse, EditorValues> {

    type = 'form';
    icon = 'fill';
    bulk = false;


    title = this.admin.translate(`EDITORS.THEME.SETTINGS.${this.group.toUpperCase()}.TITLE`);
    description = this.admin.translate(`EDITORS.THEME.SETTINGS.${this.group.toUpperCase()}.DESC`);

    entity: ThemeResponse;

    resolvedVariables: Dictionary;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public themeEngine: ThemeEngine,
        public group: string
    ) {
    }

    applyUpdate(entity: ThemeResponse, payload: ThemeUpdateRequest): Promise<ThemeResponse> {
        return this.admin.applyUpdate(api => api.entities.updateTheme(entity.id, payload), { _type: 'theme', _reload: true }).then(theme => {
            this.themeEngine.settingsToVariables(theme.settings);
            return theme;
        });
    }

    async mapEntityToEditor(entity: ThemeResponse): Promise<EditorValues> {
        this.entity = entity;
        return this.themeEngine.themeSettingsMergedWithDefault(entity.settings)[this.group];
    }

    async mapEditorToEntity(entity: ThemeUpdateRequest, values: EditorValues): Promise<ThemeUpdateRequest> {

        entity.settings = {
            ...entity.settings,
            [this.group]: {
                ...(entity.settings || {})[this.group],
                ...values,
            },
        };
        return entity;
    }

    abstract getFields(_entity: ThemeResponse): Promise<FormBuilderField[]>;



    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.THEME.SETTINGS.${key}`, values);
    }

    translateField(id: string): string {
        return this.admin.translate(`EDITORS.THEME.SETTINGS.FIELDS.${id.toUpperCase()}`);
    }

    translateValue(id: string, values?: Object): string {
        return this.admin.translate(`EDITORS.THEME.SETTINGS.VALUES.${id.toUpperCase()}`, values);
    }

    fontFamilyField(id: string, withPresetsFrom: string[], values?: Dictionary): FormBuilderField {
        const presets = { ...this.presets('font-family', withPresetsFrom), ...values, };
        return ComboField(id, this.translateField(id), true, [
            ...Object.keys(presets).map(key => ({ key: presets[key], value: key })),
            ...fonts.map(x => ({ key: x, value: x })),
        ]);
    }

    fontWeightField(id: string, withPresetsFrom: string[], values?: Dictionary): FormBuilderField {
        const presets = { ...this.presets('font-weight', withPresetsFrom), ...values, };
        return ComboField(id, this.translateField(id), true, [
            ...Object.keys(presets).map(key => ({ key: presets[key], value: key })),
            ...fontWeights.map(x => ({ key: x, value: x })),
        ]);
    }

    fontTransformField(id: string) {
        return this.comboField(id, {
            'None': 'unset',
            'Title Case': 'capitalize',
            'Upper Case': 'uppercase',
            'Lower Case': 'lowercase',
        });
    }

    dropShadowField(id: string) {
        return this.comboField(id, {
            'None': 'none',
            'Tiny': '1px 2px 5px 0 rgba(0, 0, 0, 0.1)',
            'Small': '1px 2px 5px 0 rgba(0, 0, 0, 0.2)',
            'Medium': '2px 4px 8px 0 rgba(0, 0, 0, 0.2)',
            'Large': '4px 8px 12px 0 rgba(0, 0, 0, 0.2)',
        });
    }


    comboField(id: string, values: Dictionary): FormBuilderField {
        return ComboField(id, this.translateField(id), true, Object.keys(values || {}).map(key => ({ key: values[key], value: key })));
    }

    fontColorComboField(id: string, withPresetsFrom: string[], colors?: Dictionary): FormBuilderField {
        const presets = {
            ...this.presets('font-color', withPresetsFrom),
            ...{
                [this.translateValue(`AUTO`)]: `var(--${this.group}-background-color).inverse("${THEME_OFF_WHITE}", "${THEME_OFF_BLACK}")`,
                [this.translateField(`PRIMARY.COLOR`)]: 'var(--color-primary-color)',
                [this.translateField(`PRIMARY.CONTRAST`)]: 'var(--color-primary-contrast)',
                [this.translateField(`SECONDARY.COLOR`)]: 'var(--color-secondary-color)',
                [this.translateField(`SECONDARY.CONTRAST`)]: 'var(--color-secondary-contrast)',
                [this.translateValue(`OFF_BLACK`)]: THEME_OFF_BLACK,
                [this.translateValue(`OFF_WHITE`)]: THEME_OFF_WHITE,
                [this.translateValue(`BLACK`)]: '#000000',
                [this.translateValue(`WHITE`)]: '#ffffff',
            },
            ...colors,
        };
        return this.colorComboField(id, presets);
    }

    backgroundColorField(id: string, withPresetsFrom: string[], colors: Dictionary = {}, shadeValues = [0.025, 0.05, 0.1]): FormBuilderField {
        const presets = {
            ...colors,
            ...withPresetsFrom.reduce((result, group) => {
                return {
                    ...result,
                    ...this.preset('background-color', group),
                    ...this.shadeValues('lighten', group, 'background', shadeValues),
                    ...this.shadeValues('darken', group, 'background', shadeValues),
                };
            }, {}),
            ...{
                [this.translateField(`PRIMARY.COLOR`)]: 'var(--color-primary-color)',
                [this.translateField(`SECONDARY.COLOR`)]: 'var(--color-secondary-color)',
                [this.translateValue(`WHITE`)]: '#ffffff',
                [this.translateValue(`TRANSPARENT`)]: 'transparent',
            },
        };
        return this.colorComboField(id, presets);

    }

    borderColorField(id: string, withPresetsFrom: string[], backgrounds?: string[], colors?: Dictionary): FormBuilderField {

        const presets = {
            ...colors,
            ...this.presets('border-color', withPresetsFrom),
            ...[this.group, ...(backgrounds || [])].reduce((result, group) => {
                return {
                    ...result,
                    ...this.preset('background-color', group),
                    ...this.shadeValues('lighten', group, 'background'),
                    ...this.shadeValues('darken', group, 'background'),
                };
            }, {}),
            ...{
                [this.translateField(`PRIMARY.COLOR`)]: 'var(--color-primary-color)',
                [this.translateField(`SECONDARY.COLOR`)]: 'var(--color-secondary-color)',
            },
        };
        return this.colorComboField(id, presets);

    }

    colorComboField(id: string, colors: Dictionary): FormBuilderField {
        const presets = Object.keys(colors).map(key => ({ key: colors[key], value: key }));
        return {
            type: 'colorcombo',
            id: id,
            title: this.translateField(id),
            required: true,
            values: presets,
            getText: (field, values) => {
                const val = get(values, field.id, undefined);
                const preset = presets.find(x => x.key === val);
                const color = this.computeColor(values, val);
                return preset ? `${color}:${preset.value}` : color;
            },
            options: {
                computeColor: (_field: FormBuilderField, values: any, variable: string) => {
                    return this.computeColor(values, variable);
                },
            },
        };
    }

    sizeNumberField(id: string, unit: string = 'rem'): FormBuilderField {
        return {
            id: id, title: this.translateField(id), type: 'number', required: true, min: 0, max: 100, unit: unit,
            setValue: (field, values, value) => set(values, field.id, value + unit),
            getValue: (field, values) => get(values, field.id, '1').toString().replace(unit, ''),
        };

    }

    computeColor(_values: EditorValues, variable: string): string {
        return this.themeEngine.resolveFormula(variable);
    }

    shadeValues(type: 'lighten' | 'darken', group: string, variable: 'background' | 'font', values = [0.025, 0.05, 0.1]): Dictionary {
        return values.reduce((result, val) => {
            result[this.translateValue(type.toUpperCase(), {
                group: this.admin.translate(`EDITORS.THEME.SETTINGS.${group.toUpperCase()}.TITLE`),
                variable: this.translateField(`${variable.toUpperCase()}.TITLE`),
                percentage: val * 100,
            })] = `var(--${group}-${variable}-color).${type}(${val})`;
            return result;
        }, {});
    }

    preset(type: string, group: string): Dictionary {
        return { [this.translateValue(type.toUpperCase(), { group: this.admin.translate(`EDITORS.THEME.SETTINGS.${group.toUpperCase()}.TITLE`) })]: `var(--${group}-${type})` };
    }

    presets(type: string, groups: string[]): Dictionary {
        return groups.reduce((result, group) => ({ ...result, ...this.preset(type, group) }), {});
    }


}
